// /marriage/$nationality — per-nationality marriage registration landing
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MARRIAGE_NATIONALITIES, getMarriageNationality } from "@/data/marriage-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { MarriageEmbassyLinks } from "@/components/MarriageEmbassyLinks";
import MarriageTripleKeyLinks from "@/components/MarriageTripleKeyLinks";
import ApostilleCrossLinks from "@/components/ApostilleCrossLinks";
import { Heart, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/marriage/$nationality/")({
  loader: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("ไม่พบสัญชาติ", "/marriage/$nationality", "th")}] };
    const url = `${SITE}/marriage/${n.slug}`;
    const title = `จดทะเบียนสมรสกับ${n.nameTh} ${n.flag} · เริ่ม ฿${n.serviceFeeTh.toLocaleString()} ครบจบ ${n.turnaround} | NYC`;
    const desc = `บริการจดทะเบียนสมรสกับ${n.nameTh} (${n.countryEn}) ครบทุกขั้นตอน: สถานทูต → แปลไทย → MFA → นัดอำเภอ เสร็จ ${n.turnaround} — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/marriage/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/marriage/$nationality", "th")},
        { name: "keywords", content: n.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/marriage/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/marriage/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/marriage/${n.slug}`, `/en/marriage/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `จดทะเบียนสมรสกับ${n.nameTh}`,
              serviceType: "Marriage Registration Service (International)",
              areaServed: [
                { "@type": "Country", name: "Thailand" },
                { "@type": "Country", name: n.countryEn },
              ],
              provider: {
                "@type": "LegalService", "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer", price: n.serviceFeeTh, priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          ...(n.faqs.length ? [{
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: n.faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }] : []),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธีจดทะเบียนสมรสกับ${n.nameTh}ในไทย`,
              totalTime: "P7D",
              step: [
                { "@type": "HowToStep", position: 1, name: "ปรึกษา + เตรียมเอกสาร", text: "ส่งสำเนา Passport คู่หมั้น + บัตรประชาชนไทย ผ่าน LINE @nycli" },
                { "@type": "HowToStep", position: 2, name: "Affidavit Single Status", text: `พาคู่หมั้นไป ${n.singleStatusSource} ขอ Affidavit ใช้เวลา 1-3 วัน` },
                { "@type": "HowToStep", position: 3, name: "แปลไทย + รับรอง", text: "แปล Affidavit เป็นภาษาไทยโดยนักแปลขึ้นทะเบียน + รับรอง MFA" },
                { "@type": "HowToStep", position: 4, name: "นัดอำเภอ", text: "นัดเขต/อำเภอ (แนะนำบางรัก / วัฒนา / ปทุมวัน) จดทะเบียนได้ภายใน 1 วัน" },
                { "@type": "HowToStep", position: 5, name: "รับ คร.2 + คร.3", text: "ได้ใบทะเบียนสมรส (คร.2) + ใบสำคัญ (คร.3) — แปล + Apostille เพิ่มได้สำหรับใช้ต่างประเทศ" },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "จดทะเบียนสมรส", item: `${SITE}/marriage` },
                { "@type": "ListItem", position: 3, name: `จดทะเบียนสมรสกับ${n.nameTh}`, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: MarriageNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบสัญชาติ</h1>
      <Link to="/marriage" className="mt-4 inline-block text-gold underline">กลับไปยังรายการสัญชาติทั้งหมด</Link>
          <QuotableStatsStrip cluster="marriage" locale="th" variant="light" />
    </main>
  ),
});

function MarriageNationalityPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getMarriageNationality(slug)!;
  const others = MARRIAGE_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/marriage" className="hover:text-gold">จดทะเบียนสมรส</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Heart className="h-3.5 w-3.5" /> Marriage Registration · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            จดทะเบียนสมรสกับ{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{n.notes}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${n.serviceFeeTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: n.turnaround },
              { k: "เอกสารหลัก", v: n.apostilleOnly ? "Apostille" : "Consular" },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061, ตั้งแต่ปี 2010) ให้บริการจดทะเบียนสมรสกับ{n.nameTh} ครบทุกขั้นตอน — พาไปสถานทูต ({n.singleStatusSource}) + แปลไทย + รับรอง MFA + นัดอำเภอเขตบางรัก/วัฒนา/ปทุมวัน เริ่ม ฿{n.serviceFeeTh.toLocaleString()} เสร็จ {n.turnaround} บริการสำเร็จ 2,147+ เคส
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่ต้องเตรียม</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
            {n.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอนการจดทะเบียน</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-5">
            {[
              { n: 1, t: "ปรึกษา", d: "LINE / โทร" },
              { n: 2, t: "Affidavit", d: n.singleStatusSource.split(" /")[0] },
              { n: 3, t: "แปล + MFA", d: "นักแปลขึ้นทะเบียน" },
              { n: 4, t: "นัดอำเภอ", d: "บางรัก / วัฒนา" },
              { n: 5, t: "รับ คร.2/คร.3", d: n.turnaround },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาแพ็กเกจ</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            จดทะเบียนสมรสกับ{n.nameTh} เริ่ม ฿{n.serviceFeeTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมพาไปสถานทูต + แปลไทย + MFA + นัดอำเภอ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3 วันค่าเร่ง +฿3,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ล่ามอำเภอ (ภาษาอังกฤษ / ภาษาท้องถิ่น) +฿2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เพิ่มแปล คร.2/คร.3 + Apostille สำหรับใช้ต่างประเทศ</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {n.faqs.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
              <FaqAccordion items={n.faqs} />
            </div>
          </section>
        )}

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">สัญชาติใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to="/marriage/$nationality"
                  params={{ nationality: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      {o.nameTh}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{o.serviceFeeTh.toLocaleString()} · {o.turnaround}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        <MarriageEmbassyLinks
          nationality={n.slug}
          nationalityTh={n.nameTh}
          countryTh={n.countryTh}
        />
        <MarriageTripleKeyLinks nationalityTh={n.nameTh} countryTh={n.countryTh} />
        <ApostilleCrossLinks mode="th" source="nationality" sourceSlug={n.slug} countryLabel={n.countryTh} />
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="marriage" locale="th" variant="light" />
    </main>
  );
}
